import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { DndContext, MouseSensor, TouchSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "@/lib/api";
import { PackingShare } from "@/components/PackingShare";
import { PinButton, PinNotice, useSectionPins } from "@/components/ItemPins";
import { toSentenceCase } from "@/lib/utils";
import { Plus, Trash2, Luggage, Sparkles, Copy, Download, RotateCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const uid = () => Math.random().toString(36).slice(2, 10);

const downloadPdf = async (list) => {
  const res = await api.get(`/packing/${list.id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(list.title || "packing_list").replace(/[^a-z0-9\-_ ]/gi, "").trim().replace(/\s+/g, "_")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const stats = (list) => {
  const secs = list?.sections || [];
  const all = secs.flatMap((s) => s.items || []);
  const done = all.filter((i) => i.packed).length;
  return { total: all.length, done, pct: all.length ? Math.round((done / all.length) * 100) : 0 };
};

export const PackingLists = ({ refreshKey, resumedSave }) => {
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("fresh"); // fresh | copy
  const [copyFrom, setCopyFrom] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const pins = useSectionPins("packing", refreshKey);
  const orderedLists = [...lists].sort((a, b) => Number(pins.ids.includes(b.id)) - Number(pins.ids.includes(a.id)));

  const load = async () => setLists((await api.get("/packing")).data);
  useEffect(() => { load(); }, [refreshKey]);
  const resumed = useRef(null);
  useEffect(() => {
    if (resumedSave && resumed.current !== resumedSave && lists.some((list) => list.id === resumedSave)) {
      resumed.current = resumedSave; setSelectedId(resumedSave);
    }
  }, [resumedSave, lists]);

  const resetDialog = () => { setTitle(""); setMode("fresh"); setCopyFrom(""); };

  const create = async () => {
    if (!title.trim()) return;
    if (mode === "copy" && !copyFrom) return;
    const payload = { title: toSentenceCase(title) };
    if (mode === "copy") payload.copy_from = copyFrom;
    const res = await api.post("/packing", payload);
    resetDialog();
    setOpen(false);
    setLists((prev) => [res.data, ...prev]);
  };
  const removeList = async (id) => {
    await api.delete(`/packing/${id}`);
    pins.removeDeleted(id);
    setSelectedId(null);
    setLists((prev) => prev.filter((l) => l.id !== id));
  };
  const saveList = async (list, updates) => {
    const res = await api.put(`/packing/${list.id}`, updates);
    setLists((prev) => prev.map((l) => (l.id === list.id ? res.data : l)));
  };

  const selected = lists.find((l) => l.id === selectedId) || null;

  return (
    <div data-testid="packing-lists-panel">
      <PinNotice section="packing" pins={pins} />
      <div data-testid="packing-header" className="mobile-section-header flex items-start flex-wrap gap-3 mb-8 w-full max-w-full px-5 py-4 rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <div>
          <h2 data-testid="packing-heading" className="text-2xl sm:text-3xl font-bold font-['Nunito'] text-stone-900 tracking-tight">Packing Lists</h2>
          <p data-testid="packing-tagline" className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 mt-1">Never forget a thing</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetDialog(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-packing-btn" size="icon" aria-label="New packing list" title="New packing list" className="ml-auto rounded-full bg-ac hover:bg-ac-hover text-white shrink-0 hover:-translate-y-0.5 transition-transform">
              <Plus className="w-5 h-5" strokeWidth={3} aria-hidden="true" />
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl font-['Figtree']">
            <DialogHeader><DialogTitle className="font-['Nunito'] text-2xl">New packing list</DialogTitle></DialogHeader>

            <Input data-testid="packing-title-input" placeholder="List name (e.g. Beach weekend)" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} className="rounded-xl" autoFocus />

            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                data-testid="packing-mode-fresh"
                onClick={() => setMode("fresh")}
                className={`rounded-2xl border p-3 text-left transition-all ${mode === "fresh" ? "border-ac bg-ac-soft/60" : "border-stone-200 hover:border-stone-300"}`}
              >
                <span className="flex items-center gap-2 font-bold text-stone-800 text-sm"><Sparkles className="w-4 h-4 text-ac" /> Start fresh</span>
              </button>
              <button
                type="button"
                data-testid="packing-mode-copy"
                onClick={() => setMode("copy")}
                disabled={lists.length === 0}
                className={`rounded-2xl border p-3 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${mode === "copy" ? "border-ac bg-ac-soft/60" : "border-stone-200 hover:border-stone-300"}`}
              >
                <span className="flex items-center gap-2 font-bold text-stone-800 text-sm"><Copy className="w-4 h-4 text-ac" /> Create a copy</span>
              </button>
            </div>

            {mode === "copy" && (
              <Select value={copyFrom} onValueChange={setCopyFrom}>
                <SelectTrigger data-testid="packing-copy-select" className="rounded-xl"><SelectValue placeholder="Choose a list to copy…" /></SelectTrigger>
                <SelectContent>
                  {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            <DialogFooter>
              <Button data-testid="save-packing-btn" onClick={create} disabled={!title.trim() || (mode === "copy" && !copyFrom)} className="rounded-full bg-ac hover:bg-ac-hover font-bold w-full">
                {mode === "copy" ? "Create copy" : "Create list"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {lists.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-stone-200 bg-white py-20 flex flex-col items-center text-center px-6">
          <span className="w-14 h-14 rounded-full bg-ac-soft text-ac grid place-items-center mb-4"><Luggage className="w-7 h-7" strokeWidth={2.5} /></span>
          <h3 className="text-xl font-bold font-['Nunito'] text-stone-900">No packing lists</h3>
          <p className="text-stone-500 mt-1 max-w-xs">Create a checklist so you never leave the charger behind again.</p>
        </div>
      ) : (
        <div data-testid="packing-cards" className="flex flex-col gap-4">
          {orderedLists.map((list, i) => (
              <motion.div
                key={list.id}
                data-testid={`packing-card-${list.id}`}
                data-pinned={pins.ids.includes(list.id)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="packing-summary-card group relative flex items-center gap-4 rounded-2xl border border-white/60 bg-white/95 backdrop-blur-md px-5 py-4 text-left shadow-[0_8px_24px_rgba(0,0,0,0.14)] hover:-translate-y-0.5 hover:bg-white hover:shadow-lg transition-[transform,background-color,box-shadow] duration-300"
              >
                <button type="button" data-testid={`open-packing-${list.id}`} aria-label={`Open ${list.title}`} onClick={() => setSelectedId(list.id)} className="absolute inset-0 z-[1] rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring" />
                <span className="packing-card-summary flex-1 min-w-0">
                  <span data-testid={`packing-card-title-${list.id}`} className="block font-bold font-['Nunito'] text-stone-900 text-lg leading-tight truncate">{list.title}</span>
                </span>
                <PinButton section="packing" item={list} pins={pins} />
              </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelectedId(null)}>
        {selected && <PackDetail list={selected} onSave={(list, sections) => saveList(list, { sections })} onRename={(title) => saveList(selected, { title })} onRemove={removeList} />}
      </Dialog>
    </div>
  );
};

const verticalDrag = ({ transform }) => ({ ...transform, x: 0 });
const reorderAccessibility = {
  screenReaderInstructions: { draggable: "Press Space to pick up, then Up or Down to move. Press Space to drop or Escape to cancel. Press Enter to edit the text." },
  announcements: {
    onDragStart: ({ active }) => `Picked up ${active.data.current?.label}. Use Up or Down to move.`,
    onDragOver: ({ active, over }) => over ? `Moving ${active.data.current?.label} to the position of ${over.data.current?.label}.` : undefined,
    onDragEnd: ({ active }) => `Dropped ${active.data.current?.label}.`,
    onDragCancel: () => "Reordering cancelled. Nothing changed.",
  },
};

const PackingReorderGroup = ({ items, onReorder, onDraggingChange, disabled, children, className, testId }) => {
  const suppressClick = useRef(false);
  const releaseTimer = useRef(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 400, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Tab"] } }),
  );
  useEffect(() => () => clearTimeout(releaseTimer.current), []);
  const finishDrag = () => {
    onDraggingChange(false);
    releaseTimer.current = setTimeout(() => { suppressClick.current = false; }, 250);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[verticalDrag]} accessibility={reorderAccessibility}
      onDragStart={() => { clearTimeout(releaseTimer.current); suppressClick.current = true; onDraggingChange(true); }}
      onDragCancel={finishDrag}
      onDragEnd={({ active, over }) => {
        finishDrag();
        if (!over || active.id === over.id) return;
        const from = items.findIndex((item) => item.id === active.id);
        const to = items.findIndex((item) => item.id === over.id);
        if (from !== -1 && to !== -1) onReorder(arrayMove(items, from, to));
      }}>
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy} disabled={disabled}>
        <div data-testid={testId} className={className} onClickCapture={(event) => {
          if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); }
        }}>{children}</div>
      </SortableContext>
    </DndContext>
  );
};

const PackDetail = ({ list, onSave, onRename, onRemove }) => {
  const [newSection, setNewSection] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pendingSections, setPendingSections] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const sections = pendingSections || list.sections || [];
  const { total, done, pct } = stats(list);

  const saveOrder = async (nextSections) => {
    setPendingSections(nextSections);
    setSavingOrder(true);
    setOrderStatus(null);
    try {
      await onSave(list, nextSections);
    } catch {
      setOrderStatus({ error: true, text: "Couldn't save the new order. Your previous order has been restored. Please try again." });
    } finally {
      setPendingSections(null);
      setSavingOrder(false);
    }
  };

  const update = (fn) => onSave(list, fn(sections));

  const addSection = () => {
    const t = newSection.trim();
    if (!t) return;
    update((s) => [...s, { id: uid(), title: toSentenceCase(t), items: [] }]);
    setNewSection("");
  };
  const removeSection = (sid) => update((s) => s.filter((x) => x.id !== sid));
  const renameSection = (sid, title) => update((s) => s.map((x) => x.id === sid ? { ...x, title } : x));
  const editItem = (sid, iid, text) => update((s) => s.map((x) => x.id === sid ? { ...x, items: x.items.map((i) => i.id === iid ? { ...i, text } : i) } : x));
  const addItem = (sid, text) => update((s) => s.map((x) => x.id === sid ? { ...x, items: [...x.items, { id: uid(), text: toSentenceCase(text), packed: false }] } : x));
  const toggleItem = (sid, iid) => update((s) => s.map((x) => x.id === sid ? { ...x, items: x.items.map((i) => i.id === iid ? { ...i, packed: !i.packed } : i) } : x));
  const removeItem = (sid, iid) => update((s) => s.map((x) => x.id === sid ? { ...x, items: x.items.filter((i) => i.id !== iid) } : x));
  const resetAll = () => update((s) => s.map((x) => ({ ...x, items: x.items.map((i) => ({ ...i, packed: false })) })));

  return (
    <DialogContent data-testid="packing-detail-dialog" onEscapeKeyDown={(event) => { if (dragging || event.target.closest("[data-packing-editor]")) event.preventDefault(); }} className="rounded-3xl font-['Figtree'] max-h-[88vh] overflow-y-auto">
      <fieldset disabled={savingOrder} className="contents">
      <DialogHeader className="text-left min-w-0 pr-5">
        <DialogTitle className={editingTitle ? "sr-only" : "font-['Nunito'] text-2xl flex items-center gap-2 min-w-0"}>
          {editingTitle ? list.title : <>
            <button type="button" data-testid={`packing-title-${list.id}`} onClick={() => setEditingTitle(true)} title="Tap to edit list name" aria-label={`Edit list name: ${list.title}`} className="min-w-0 text-left break-words rounded-md hover:text-ac focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors">{list.title}</button>
          </>}
        </DialogTitle>
        {editingTitle && <PackTextEditor initialText={list.title} onSave={onRename} onCancel={() => setEditingTitle(false)} testId={`edit-list-${list.id}`} label="List name" />}
        <DialogDescription data-testid="packing-edit-hint" className="text-xs text-stone-400">Tap text to edit. Hold an item or section heading to drag it into a new position.</DialogDescription>
        <p data-testid="packing-reorder-keyboard-hint" className="sr-only">Keyboard: focus item text or a section heading, press Space, use Up or Down, then Space to drop. Escape cancels.</p>
      </DialogHeader>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 w-full sm:w-auto sm:flex-1 min-w-0">
          <Progress value={pct} className="h-2 flex-1" />
          <span className="text-xs font-bold text-stone-500 whitespace-nowrap">{done}/{total} packed</span>
        </div>
        <PackingShare list={list} />
        <Button data-testid={`download-packing-${list.id}`} onClick={() => downloadPdf(list)} variant="outline" size="sm" className="rounded-full font-bold text-stone-700 shrink-0">
          <Download className="w-4 h-4 mr-1.5" /> PDF
        </Button>
      </div>

      {(savingOrder || orderStatus) && <p data-testid="packing-reorder-status" role={orderStatus?.error ? "alert" : "status"} className={`text-xs ${orderStatus?.error ? "text-rose-600" : "text-ac-softfg"}`}>{savingOrder ? "Saving order…" : orderStatus.text}</p>}
      <PackingReorderGroup items={sections} onReorder={saveOrder} onDraggingChange={setDragging} disabled={savingOrder} className="space-y-5 mt-2" testId="packing-sections-reorder">
        {sections.map((sec) => (
          <PackSectionBlock
            key={sec.id}
            section={sec}
            onAddItem={(text) => addItem(sec.id, text)}
            onToggle={(iid) => toggleItem(sec.id, iid)}
            onRemoveItem={(iid) => setDeleteTarget({ kind: "item", sectionId: sec.id, itemId: iid, name: sec.items.find((item) => item.id === iid).text })}
            onEditItem={(iid, text) => editItem(sec.id, iid, text)}
            onRenameSection={(title) => renameSection(sec.id, title)}
            onRemoveSection={() => setDeleteTarget({ kind: "section", sectionId: sec.id, name: sec.title })}
            onReorderItems={(items) => saveOrder(sections.map((section) => section.id === sec.id ? { ...section, items } : section))}
            onDraggingChange={setDragging}
            reorderDisabled={savingOrder}
          />
        ))}
        {sections.length === 0 && <p className="text-stone-400 text-sm text-center py-4">No sections yet — add one below.</p>}
      </PackingReorderGroup>

      {/* Add section */}
      <div className="flex gap-2 mt-1 pt-3 border-t border-stone-100">
        <div className="relative flex-1">
          <Input data-testid={`add-section-input-${list.id}`} placeholder="New section" value={newSection} onChange={(e) => setNewSection(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSection()} className="rounded-xl" />
        </div>
        <Button data-testid={`add-section-btn-${list.id}`} onClick={addSection} variant="outline" className="rounded-xl font-bold">Add section</Button>
      </div>

      <DialogFooter>
        <div className="flex flex-col gap-2 w-full">
          <div className="flex gap-2 w-full">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid={`reset-packing-${list.id}`} disabled={done === 0} variant="outline" className="rounded-full font-bold text-stone-700 flex-1">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset list
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-3xl font-['Figtree']">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-['Nunito']">Reset this list?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will uncheck all packed items in “{list.title}”. Your items stay — only the progress resets.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full font-bold">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid={`confirm-reset-${list.id}`} onClick={resetAll} className="rounded-full font-bold bg-ac hover:bg-ac-hover">Reset</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {!list.is_default && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid={`delete-packing-${list.id}`} variant="outline" className="rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 flex-1">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete list
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl font-['Figtree']">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-['Nunito']">Delete this list?</AlertDialogTitle>
                    <AlertDialogDescription>
                      “{list.title}” and all its sections and items will be permanently deleted. This can't be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full font-bold">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid={`confirm-delete-${list.id}`} onClick={() => onRemove(list.id)} className="rounded-full font-bold bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          {list.is_default && (
            <p className="text-xs text-stone-400 text-center w-full py-0.5">This is a default list — you can edit it, but it can't be deleted.</p>
          )}
        </div>
      </DialogFooter>
      </fieldset>
      {deleteTarget && <PackDeleteConfirmation target={deleteTarget} listId={list.id} onCancel={() => setDeleteTarget(null)} onConfirm={() => deleteTarget.kind === "section" ? removeSection(deleteTarget.sectionId) : removeItem(deleteTarget.sectionId, deleteTarget.itemId)} />}
    </DialogContent>
  );
};

const PackDeleteConfirmation = ({ target, listId, onConfirm, onCancel }) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const deleting = useRef(false);
  const isSection = target.kind === "section";
  const confirm = async (event) => {
    event.preventDefault();
    if (deleting.current) return;
    deleting.current = true;
    setPending(true); setError("");
    try { await onConfirm(); onCancel(); }
    catch { setError("Couldn't delete. Please try again."); }
    finally { deleting.current = false; setPending(false); }
  };
  return <AlertDialog open onOpenChange={(open) => { if (!open && !pending) onCancel(); }}>
    <AlertDialogContent data-testid="packing-entry-delete-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']" onEscapeKeyDown={(event) => { event.stopPropagation(); if (pending) event.preventDefault(); }} onCloseAutoFocus={(event) => {
      event.preventDefault();
      const trigger = isSection ? `remove-section-${target.sectionId}` : `remove-pack-${target.itemId}`;
      const fallback = isSection ? `add-section-input-${listId}` : "packing-detail-dialog";
      const triggerElement = document.querySelector(`[data-testid="${trigger}"]`);
      const focusTarget = triggerElement || document.querySelector(`[data-testid="${fallback}"]`);
      focusTarget?.focus(!isSection && !triggerElement ? { preventScroll: true } : undefined);
    }}>
      <AlertDialogHeader>
        <AlertDialogTitle data-testid="packing-entry-delete-title" className="font-['Nunito']">Delete {isSection ? "section" : "item"}?</AlertDialogTitle>
        <AlertDialogDescription data-testid="packing-entry-delete-description" className="break-words">Delete “{target.name}”{isSection ? " and all its items" : ""} from this packing list? This can't be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      {error && <p data-testid="packing-entry-delete-error" role="alert" className="text-sm text-rose-600">{error}</p>}
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="packing-entry-delete-cancel" disabled={pending} className="rounded-full font-bold">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="packing-entry-delete-confirm" disabled={pending} onClick={confirm} className="rounded-full font-bold bg-rose-600 hover:bg-rose-700">{pending ? "Deleting…" : "Delete"}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
};

const PackTextEditor = ({ initialText, onSave, onCancel, testId, label, selectOnFocus = true }) => {
  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (event) => {
    event.preventDefault();
    if (!text.trim() || saving) return;
    setSaving(true);
    setError("");
    try {
      if (text.trim() !== initialText.trim()) await onSave(toSentenceCase(text));
      onCancel();
    } catch {
      setError("Couldn't save your changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-packing-editor data-testid={`${testId}-form`} onSubmit={save} onKeyDown={(event) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); if (!saving) onCancel(); }
    }} className="w-full min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid={`${testId}-input`} aria-label={label} value={text} onChange={(event) => setText(event.target.value)} disabled={saving} autoFocus onFocus={(event) => {
          const input = event.target;
          if (selectOnFocus) input.select();
          else input.setSelectionRange(input.value.length, input.value.length);
        }} onContextMenu={selectOnFocus ? undefined : (event) => event.preventDefault()} style={selectOnFocus ? undefined : { WebkitTouchCallout: "none" }} className="min-w-0 flex-1 basis-40 rounded-xl h-9 text-sm" />
        <div className="flex gap-1 shrink-0">
          <Button data-testid={`${testId}-save`} type="submit" disabled={!text.trim() || saving} size="sm" className="rounded-xl bg-ac hover:bg-ac-hover text-white font-bold">{saving ? "Saving…" : "Save"}</Button>
          <Button data-testid={`${testId}-cancel`} type="button" onClick={onCancel} disabled={saving} variant="ghost" size="sm" className="rounded-xl text-stone-600">Cancel</Button>
        </div>
      </div>
      {error && <p data-testid={`${testId}-error`} role="alert" className="text-xs text-rose-600">{error}</p>}
    </form>
  );
};

const PackItemRow = ({ item, onEdit, onToggle, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const drag = useSortable({ id: item.id, disabled: editing, data: { label: item.text } });
  return (
    <div ref={drag.setNodeRef} data-testid={`pack-item-${item.id}`} data-dragging={drag.isDragging || undefined} style={{ transform: CSS.Translate.toString(drag.transform), transition: drag.transition, zIndex: drag.isDragging ? 20 : undefined }} className={`relative flex items-center gap-2 py-1 min-w-0 rounded-lg ${drag.isDragging ? "bg-ac-soft shadow-lg ring-1 ring-ac" : ""}`}>
      {editing ? <PackTextEditor initialText={item.text} onSave={onEdit} onCancel={() => setEditing(false)} testId={`edit-pack-${item.id}`} label="Item name" selectOnFocus={false} /> : <>
        <Checkbox checked={item.packed} onCheckedChange={onToggle} data-testid={`pack-check-${item.id}`} aria-label={`Mark ${item.text} as ${item.packed ? "unpacked" : "packed"}`} className="shrink-0 data-[state=checked]:bg-ac data-[state=checked]:border-ac rounded-md" />
        <button ref={drag.setActivatorNodeRef} {...drag.attributes} {...drag.listeners} type="button" data-testid={`pack-text-${item.id}`} onClick={() => setEditing(true)} onContextMenu={(event) => event.preventDefault()} style={{ WebkitTouchCallout: "none" }} title="Tap to edit; hold to reorder" aria-label={`Edit or reorder item: ${item.text}`} className={`flex-1 min-w-0 min-h-8 text-left break-words text-sm rounded-md select-none touch-manipulation cursor-grab active:cursor-grabbing hover:text-ac focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors ${item.packed ? "line-through text-stone-400" : "text-stone-700"}`}>{item.text}</button>
        <button type="button" data-testid={`remove-pack-${item.id}`} onClick={onRemove} title="Delete item" aria-label={`Delete ${item.text}`} className="shrink-0 w-8 h-8 grid place-items-center rounded-lg text-stone-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
      </>}
    </div>
  );
};

const PackSectionBlock = ({ section, onAddItem, onToggle, onRemoveItem, onEditItem, onRenameSection, onRemoveSection, onReorderItems, onDraggingChange, reorderDisabled }) => {
  const [text, setText] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const drag = useSortable({ id: section.id, disabled: editingTitle, data: { label: section.title } });
  const items = section.items || [];
  const done = items.filter((i) => i.packed).length;

  const add = () => { if (text.trim()) { onAddItem(text.trim()); setText(""); } };

  return (
    <div ref={drag.setNodeRef} data-testid={`section-${section.id}`} data-dragging={drag.isDragging || undefined} style={{ transform: CSS.Translate.toString(drag.transform), transition: drag.transition, zIndex: drag.isDragging ? 20 : undefined }} className={`relative min-w-0 rounded-xl ${drag.isDragging ? "bg-white shadow-xl ring-1 ring-ac" : ""}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5 min-w-0">
        {editingTitle ? <PackTextEditor initialText={section.title} onSave={onRenameSection} onCancel={() => setEditingTitle(false)} testId={`edit-section-${section.id}`} label="Section heading" selectOnFocus={false} /> : <>
          <h4 className="min-w-0 flex-1 text-sm font-black text-ac-softfg flex items-center gap-2">
            <button ref={drag.setActivatorNodeRef} {...drag.attributes} {...drag.listeners} type="button" data-testid={`section-title-${section.id}`} onClick={() => setEditingTitle(true)} onContextMenu={(event) => event.preventDefault()} style={{ WebkitTouchCallout: "none" }} title="Tap to edit; hold to reorder section" aria-label={`Edit or reorder heading: ${section.title}`} className="min-w-0 min-h-8 text-left break-words rounded-md select-none touch-manipulation cursor-grab active:cursor-grabbing hover:text-ac focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors">{section.title}</button>
            <span data-testid={`section-count-${section.id}`} className="shrink-0 text-xs text-stone-400 font-bold">{done}/{items.length}</span>
          </h4>
          <button type="button" data-testid={`remove-section-${section.id}`} onClick={onRemoveSection} title="Delete section" aria-label={`Delete section ${section.title}`} className="shrink-0 w-8 h-8 grid place-items-center rounded-lg text-stone-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
        </>}
      </div>

      <PackingReorderGroup items={items} onReorder={onReorderItems} onDraggingChange={onDraggingChange} disabled={reorderDisabled} className="space-y-0.5" testId={`packing-items-reorder-${section.id}`}>
        {items.map((item) => <PackItemRow key={item.id} item={item} onEdit={(value) => onEditItem(item.id, value)} onToggle={() => onToggle(item.id)} onRemove={() => onRemoveItem(item.id)} />)}
      </PackingReorderGroup>

      <div className="flex gap-2 mt-1.5">
        <Input data-testid={`pack-input-${section.id}`} placeholder="Add item…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="rounded-xl h-9 text-sm" />
        <Button data-testid={`pack-add-${section.id}`} onClick={add} className="rounded-xl bg-stone-900 hover:bg-stone-800 px-3 h-9"><Plus className="w-4 h-4" strokeWidth={3} /></Button>
      </div>
    </div>
  );
};
